import React, { useRef, useEffect, useState } from 'react';
import {
  OverlayTooltip,
  Placement,
} from '../../design-system/components/overlay';
import type { TruncatedTextWithAutoTooltipProps } from './types';

/**
 * This component can be used to show a tooltip when the text is truncated.
 */

const TruncatedTextWithAutoTooltip: React.FC<TruncatedTextWithAutoTooltipProps> = ({
  text,
  className = '',
  placement = Placement.Bottom,
  tooltipClassName = '',
  children,
  redirectUrl,
}) => {
  const textRef = useRef<HTMLSpanElement>(null);
  const [isTextTruncated, setIsTextTruncated] = useState(false);

  useEffect(() => {
    const checkTruncation = () => {
      if (textRef.current) {
        const element = textRef.current;
        // Check if text is truncated by comparing scrollWidth with clientWidth
        const isTruncated = element.scrollWidth > element.clientWidth;
        setIsTextTruncated(isTruncated);
      }
    };

    checkTruncation();

    // Recheck on window resize with debounce
    let resizeTimeout: NodeJS.Timeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(checkTruncation, 150); // Debounced resize check
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('resize', handleResize);
    }

    // Use ResizeObserver if available for more accurate detection
    let resizeObserver: ResizeObserver | null = null;
    if (
      typeof window !== 'undefined' &&
      window.ResizeObserver &&
      textRef.current
    ) {
      try {
        resizeObserver = new ResizeObserver(() => {
          setTimeout(checkTruncation, 100);
        });
        resizeObserver.observe(textRef.current);
      } catch (error) {
        // Fallback if ResizeObserver fails - silently continue
      }
    }

    return () => {
      clearTimeout(resizeTimeout);
      if (typeof window !== 'undefined') {
        window.removeEventListener('resize', handleResize);
      }
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [text]);

  const truncationStyles: React.CSSProperties = {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    display: 'inline-block',
    minWidth: 0,
    maxWidth: '100%',
  };

  const textElement = redirectUrl ? (
    <a
      ref={textRef as React.RefObject<HTMLAnchorElement>}
      href={redirectUrl}
      target="_blank"
      rel="noreferrer"
      className={className}
      style={truncationStyles}
    >
      {children || text}
    </a>
  ) : (
    <span
      ref={textRef}
      className={className}
      style={truncationStyles}
    >
      {children || text}
    </span>
  );

  if (isTextTruncated) {
    return (
      <OverlayTooltip
        text={text}
        placement={placement}
        className={tooltipClassName}
      >
        {textElement}
      </OverlayTooltip>
    );
  }

  return textElement;
};

export default TruncatedTextWithAutoTooltip;
